import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper, Box, Typography, TextField, Switch, Button, CircularProgress, Alert, FormControlLabel,
} from '@mui/material';
import { CardGiftcardRounded, GroupRounded, ConfirmationNumberRounded, EventRounded } from '@mui/icons-material';
import { PageSub, SectionCard, Grid } from '../../components/common';
import StatCard from '../../components/StatCard';
import { useToast } from '../../components/Toast';
import { useTrialSettings, TrialSettings } from '../../hooks/useTrialSettings';
import { colors } from '../../theme/theme';

interface NumField {
  key: 'trial_days' | 'provider_count' | 'token_count';
  label: string;
  help: string;
  min: number;
  max: number;
}

const FIELDS: NumField[] = [
  { key: 'trial_days', label: 'Trial period (days)', help: 'How long the free trial runs from the day the business is created.', min: 1, max: 365 },
  { key: 'provider_count', label: 'Providers included', help: 'Team members the business can assign to queues during the trial.', min: 1, max: 100 },
  { key: 'token_count', label: 'Tokens included', help: 'Tokens per day the business can offer across all its queues during the trial.', min: 1, max: 100000 },
];

/** Blank while typing is allowed; validation runs on save. */
type Draft = { is_enabled: boolean; trial_days: string; provider_count: string; token_count: string };

const toDraft = (s: TrialSettings): Draft => ({
  is_enabled: s.is_enabled,
  trial_days: String(s.trial_days),
  provider_count: String(s.provider_count),
  token_count: String(s.token_count),
});

const TrialSettingsPage: React.FC = () => {
  const toast = useToast();
  const { settings, loading, saving, error, reload, save } = useTrialSettings();
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (settings) {
      setDraft(toDraft(settings));
    }
  }, [settings]);

  const fieldError = (f: NumField, raw: string): string | null => {
    if (raw.trim() === '') {
      return 'Required';
    }
    const n = Number(raw);
    if (!Number.isInteger(n) || n < f.min || n > f.max) {
      return `Enter a whole number between ${f.min} and ${f.max}`;
    }
    return null;
  };

  const errors = useMemo(
    () => (draft ? FIELDS.map((f) => fieldError(f, draft[f.key])) : []),
    [draft]
  );
  const invalid = errors.some((e) => e !== null);
  const dirty = !!settings && !!draft && JSON.stringify(draft) !== JSON.stringify(toDraft(settings));

  const handleSave = async () => {
    if (!draft || invalid) {
      return;
    }
    const msg = await save({
      is_enabled: draft.is_enabled,
      trial_days: Number(draft.trial_days),
      provider_count: Number(draft.provider_count),
      token_count: Number(draft.token_count),
    });
    toast(msg ?? 'Trial settings saved', msg ? 'error' : 'success');
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !settings || !draft) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'Not available.'}</Alert>;
  }

  return (
    <>
      <PageSub>
        These values are granted as a free trial the moment a provider creates a business — no payment involved.
        Changes apply to <b>businesses created from now on</b>; trials already running keep what they were given
        (edit those from the business's <b>Subscription &amp; Billing</b> tab).
      </PageSub>

      <Grid cols={3} sx={{ mb: 2.5 }}>
        <StatCard icon={<EventRounded />} color="#7C3AED" value={settings.is_enabled ? `${settings.trial_days} days` : 'Off'} label="Trial period" />
        <StatCard icon={<GroupRounded />} color="#076FF7" value={settings.provider_count} label="Providers included" />
        <StatCard icon={<ConfirmationNumberRounded />} color="#16A34A" value={settings.token_count} label="Tokens included" />
      </Grid>

      <SectionCard title="Free Trial Offer">
        <FormControlLabel
          sx={{ mb: 1 }}
          control={
            <Switch
              color="success"
              checked={draft.is_enabled}
              onChange={(e) => setDraft({ ...draft, is_enabled: e.target.checked })}
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Grant a free trial on business creation</Typography>
              <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
                Off — new businesses stay pending and must buy a plan before their queues can go live.
              </Typography>
            </Box>
          }
        />

        <Box
          sx={{
            display: 'grid', gap: 2.5, mt: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            opacity: draft.is_enabled ? 1 : 0.5,
            pointerEvents: draft.is_enabled ? 'auto' : 'none',
          }}
        >
          {FIELDS.map((f, i) => (
            <TextField
              key={f.key}
              label={f.label}
              value={draft[f.key]}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value.replace(/[^0-9]/g, '') })}
              error={errors[i] !== null}
              helperText={errors[i] ?? f.help}
              inputProps={{ inputMode: 'numeric' }}
              size="small"
              fullWidth
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<CardGiftcardRounded />}
            disabled={saving || invalid || !dirty}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save trial settings'}
          </Button>
          <Button
            variant="outlined"
            disabled={saving || !dirty}
            onClick={() => setDraft(toDraft(settings))}
            sx={{ borderColor: colors.border, color: colors.textPrimary }}
          >
            Discard changes
          </Button>
          {settings.updated_at && (
            <Typography sx={{ fontSize: 12.5, color: colors.textTertiary }}>
              Last updated {new Date(settings.updated_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      </SectionCard>

      <Paper sx={{ p: 2.5, mt: 2.5, bgcolor: colors.primaryLight, border: `1px solid ${colors.border}` }}>
        <Typography sx={{ fontSize: 13.5, color: colors.textSecondary }}>
          The trial counts as a real subscription, so provider and token limits, queue activation and expiry all
          behave exactly like a paid plan — it simply costs nothing and carries no Razorpay reference. Buying a plan
          replaces the trial immediately.
        </Typography>
      </Paper>
    </>
  );
};

export default TrialSettingsPage;
